import { useState } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { ApiError, workspaces } from "../lib/api";

// Slug rule mirrors the server: 2..32 chars, lowercase alnum + hyphen,
// not starting/ending with hyphen. Server is authoritative; client
// pre-validates to avoid a round trip.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

function Onboarding() {
  const { t } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errs, setErrs] = useState<{ name?: string; slug?: string; form?: string }>({});

  if (s.state === "loading") return null;
  if (s.state === "anon") return <Navigate to="/login" replace />;
  // Already have at least one workspace -> skip onboarding.
  if (s.me.workspaces.length > 0) return <Navigate to="/app" replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: typeof errs = {};
    if (name.trim().length < 1) next.name = t("onboarding.name");
    if (!SLUG_RE.test(slug)) next.slug = t("onboarding.slugHint");
    setErrs(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const ws = await workspaces.create({
        name: name.trim(),
        slug,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await workspaces.switch({ workspace_id: ws.id });
      await s.refresh();
      void nav({ to: "/app", replace: true });
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) {
        setErrs({ slug: t("onboarding.slugTaken") });
      } else {
        setErrs({ form: t("common.errorGeneric") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-full grid place-items-center px-4 py-12">
      <div className="w-full max-w-md flex flex-col gap-6">
        <div className="flex justify-center"><Logo size="lg" /></div>
        <Card>
          <span className="eyebrow eyebrow-pill mb-3 inline-flex">İLK ADIM · STEP 1</span>
          <h1 className="font-display text-2xl font-semibold mb-1">{t("onboarding.title")}</h1>
          <p className="text-secondary text-sm mb-6">{t("onboarding.subtitle")}</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <Input
              label={t("onboarding.name")}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              errorText={errs.name ?? null}
              maxLength={100}
            />
            <Input
              label={t("onboarding.slug")}
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              hint={t("onboarding.slugHint")}
              errorText={errs.slug ?? null}
              pattern="[a-z0-9][a-z0-9-]{1,30}[a-z0-9]"
            />
            {errs.form ? <p className="text-error text-sm" role="alert">{errs.form}</p> : null}
            <Button type="submit" loading={submitting} full>{t("onboarding.submit")}</Button>
          </form>
        </Card>
      </div>
    </main>
  );
}

export const Route = createFileRoute("/onboarding")({
  component: Onboarding,
});
